import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import { AuthContext } from "../context/AuthContext";
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  LifebuoyIcon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  checkEcu,
  downloadBlob,
  dtcRowToPairs,
  extractApiError,
  extractAlreadyOff,
  extractDtcRows,
  extractEcuCandidates,
  extractOnSolutions,
  hasDtc2Column,
  isEcuSelectionPayload,
  patchEcu,
} from "../utils/automaticApi";
import { ecuBinaryRejection } from "../utils/ecuFileGuard";
import EcuTicketRequestModal from "../components/EcuTicketRequestModal";
import NoteContent from "../components/NoteContent";
import { noteToPlainText } from "../utils/noteHtml";
import { SOLUTION_MODULES, TUNING_STAGES } from "../utils/solutionModules";
import { SolutionIcon, TuningIcon } from "../utils/moduleIcons";
import {
  Badge,
  CONTROL,
  CreditsPill,
  EmptyState,
  GroupLabel,
  InfoTile,
  PANEL,
  SUNKEN,
  SectionHeading,
  Spinner,
  Switch,
  buttonClass,
} from "../components/ui";

const priceLabel = (credits) => (credits == null ? "" : `${credits} CRS`);

const PRIMARY = buttonClass();
const GHOST = buttonClass({ variant: "ghost" });

// Splits a cell that packs several codes so the columns line up like a data grid.
const formatDtcCode = (code) => String(code || "").trim().split(/\s+/).join("  ");

// A solution/tuning tile. `active` = switched on for the patch (red = it will be
// applied and charged); the default state is the untouched, brand-tinted tile.
// `alreadyOff` is the check's own verdict — the function is already gone from
// the uploaded file, so the tile reads off and there is nothing left to sell.
// `note`: an installer note for this solution on this ECU (from the check) — flagged on the tile.
const ToggleTile = ({ label, price, icon, active, disabled, alreadyOff, onToggle, note }) => {
  const inert = disabled || alreadyOff;
  const state = inert
    ? {
        card: "border-gray-200 bg-gray-50 dark:border-[#2c2c2e] dark:bg-[#161617]",
        icon: "bg-gray-100 text-gray-400 dark:bg-[#262628] dark:text-slate-500",
        text: "text-gray-400 dark:text-slate-500",
        priceText: "text-gray-400 dark:text-slate-500",
      }
    : active
    ? {
        card: "border-rose-500/40 bg-rose-50 dark:border-rose-500/30 dark:bg-[#1f1a1c]",
        icon: "bg-rose-500/15 text-rose-500 dark:text-rose-400",
        text: "text-rose-700 dark:text-rose-300",
        priceText: "text-rose-600 dark:text-rose-300",
      }
    : {
        card: "border-gray-200 bg-white hover:border-red-300 dark:border-[#3a3a3d]/70 dark:bg-[#262628] dark:hover:border-orange-400/40",
        icon: "bg-gradient-to-br from-red-500/15 to-orange-500/15 text-red-600 dark:text-orange-400",
        text: "text-gray-800 dark:text-slate-100",
        priceText: "text-red-600 dark:text-orange-300",
      };

  return (
    <div className={`flex items-center gap-2 rounded-[12px] border p-2 transition ${state.card}`}>
      <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px] ${state.icon}`}>
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <p className={`flex items-center gap-1 text-[11px] font-semibold leading-tight ${state.text}`} title={label}>
          <span className="truncate">{label}</span>
          {note ? (
            <ExclamationTriangleIcon
              className="h-3.5 w-3.5 shrink-0 text-amber-500 dark:text-amber-400"
              title={noteToPlainText(note)}
              aria-label="Has an installer note"
            />
          ) : null}
        </p>
        {alreadyOff ? (
          <span className={`text-[10px] font-bold ${state.priceText}`}>Already off</span>
        ) : price == null ? null : (
          <span className={`text-[10px] font-bold ${state.priceText}`}>{priceLabel(price)}</span>
        )}
      </div>
      <Switch
        checked={!active && !alreadyOff}
        disabled={inert}
        onToggle={onToggle}
        label={label}
        small
      />
    </div>
  );
};

const DtcCodeChip = ({ code, off }) => (
  <strong
    className={`inline-block w-fit whitespace-pre rounded-[10px] px-2 py-1 font-mono text-xs font-bold ${
      off
        ? "bg-rose-500 text-white"
        : "bg-gray-100 text-gray-700 dark:bg-[#262628] dark:text-slate-200"
    }`}
  >
    {code ? formatDtcCode(code) : "-----"}
  </strong>
);

const Automatic = () => {
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [brand, setBrand] = useState("");
  const [ecu, setEcu] = useState("");

  const [checking, setChecking] = useState(false);
  const [quote, setQuote] = useState(null);
  const [needsSelection, setNeedsSelection] = useState(false);
  // The brand/ECU pairs a 409 offered, and whether the picker is open.
  const [ecuCandidates, setEcuCandidates] = useState([]);
  const [isEcuModalOpen, setIsEcuModalOpen] = useState(false);
  // The server's own wording for the ambiguity, shown in the picker.
  const [ecuSelectionMessage, setEcuSelectionMessage] = useState("");
  const [error, setError] = useState("");
  // Support request (Automatic support ticket) about the uploaded file: the form, and the sent notice.
  const [isRequestOpen, setIsRequestOpen] = useState(false);
  const [requestSent, setRequestSent] = useState(false);

  const [selectedModules, setSelectedModules] = useState(() => new Set());
  const [selectedTunings, setSelectedTunings] = useState(() => new Set());
  const [dtcOffSet, setDtcOffSet] = useState(() => new Set());
  const [dtcSearch, setDtcSearch] = useState("");
  const [offDtcSearch, setOffDtcSearch] = useState("");
  const [isOffDtcModalOpen, setIsOffDtcModalOpen] = useState(false);

  const [patching, setPatching] = useState(false);
  const [patchResult, setPatchResult] = useState(null);

  const [isDragging, setIsDragging] = useState(false);
  // Shown in a blocking modal when the picked file isn't a raw ECU binary.
  const [fileError, setFileError] = useState("");

  const [creditBalance, setCreditBalance] = useState(null);
  const [modalMsg, setModalMsg] = useState("");
  const { user } = useContext(AuthContext);
  const unlimited = String(user?.role || "").toLowerCase() === "admin";

  const refreshBalance = () =>
    axiosInstance
      .get("/users/my")
      .then((res) => setCreditBalance(Number(res.data?.credit_balance ?? 0)))
      .catch(() => {});
  useEffect(() => {
    refreshBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const busy = checking || patching;

  const info = quote?.identification?.ecuinfo || quote?.identification?.ecuInfo || {};
  const pricing = quote?.pricing || {};
  const moduleEntries = useMemo(() => Object.entries(pricing.modules || {}), [pricing.modules]);
  // Memoized so the identity is stable between renders — totalCost's useMemo
  // depends on it and would otherwise recompute on every render.
  const tunings = useMemo(() => pricing.tunings || [], [pricing.tunings]);
  // The solutions/tunings the check reports as already off in this file. They
  // stay on screen (the customer can see they are done) but cannot be toggled.
  // Installer notes the check attached to solutions (Automated Modules), by solution name.
  const solutionNotes = useMemo(() => {
    const notes = {};
    for (const solution of quote?.identification?.solutions || []) {
      const name = String(solution?.name ?? "").trim();
      const note = String(solution?.note ?? "").trim();
      if (name && note) notes[name] = note;
    }
    return notes;
  }, [quote]);
  // A solution with a note waiting for the customer to confirm it before it is selected.
  const [pendingNote, setPendingNote] = useState(null);
  const solutionsAlreadyOff = useMemo(
    () => extractAlreadyOff(quote?.identification?.solutions),
    [quote]
  );
  const tuningsAlreadyOff = useMemo(
    () => extractAlreadyOff(quote?.identification?.tunings),
    [quote]
  );
  const dtcRows = useMemo(() => extractDtcRows(quote?.identification?.dtcTable), [quote]);
  // Each solution's own DTC group from the check (identification.solutions[].dtc), as rows.
  const solutionDtcs = useMemo(() => {
    const groups = {};
    for (const solution of quote?.identification?.solutions || []) {
      const name = String(solution?.name ?? "").trim();
      const rows = extractDtcRows(solution?.dtc);
      if (name && rows.length > 0) groups[name] = rows;
    }
    return groups;
  }, [quote]);
  // For each solution, the DTC-table rows (by index) its group points at — matched by code, as
  // EcuMatic does (the solution group and the table come from separate row sequences). Rows the
  // file already has off are left out: there is nothing to turn off.
  const solutionDtcIndexes = useMemo(() => {
    // Matched on the DTC1+DTC2 pair — the check lists a solution's group as the file's own DTC
    // table rows, and /patch prices it on the same pair.
    const col = (code) => {
      const text = String(code || "").trim().split(/\s+/).join(" ").toUpperCase();
      return /^[-\s]*$/.test(text) ? "" : text;
    };
    const key = (row) => `${col(row.code1)}|${col(row.code2)}`;
    const byName = {};
    for (const [name, rows] of Object.entries(solutionDtcs)) {
      const pairs = new Set(rows.map(key).filter((k) => k !== "|"));
      byName[name] = dtcRows
        .map((row, index) => ({ row, index }))
        .filter(({ row }) => !row.alreadyOff && pairs.has(key(row)))
        .map(({ index }) => index);
    }
    return byName;
  }, [solutionDtcs, dtcRows]);
  const hasDtc2 = useMemo(() => hasDtc2Column(dtcRows), [dtcRows]);
  // Keep the original index so the dtcOffSet (keyed by row index) stays valid while filtering.
  const visibleDtcRows = useMemo(() => {
    const rows = dtcRows.map((row, index) => ({ row, index }));
    const q = dtcSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      ({ row }) =>
        (row.code1 || "").toLowerCase().includes(q) ||
        (row.code2 || "").toLowerCase().includes(q) ||
        (row.description || "").toLowerCase().includes(q)
    );
  }, [dtcRows, dtcSearch]);
  const swNumber =
    info.softwareNumber ||
    (Array.isArray(info.softwareNumbers) ? info.softwareNumbers[0] : "") ||
    "";

  const dtcActive = dtcOffSet.size > 0;
  // DTC rows that come with a selected solution (its DTC group) — included in that solution's
  // price. DTC off is only charged when a row beyond those is switched off, as /patch prices it.
  const includedDtcs = useMemo(
    () => new Set(Array.from(selectedModules).flatMap((name) => solutionDtcIndexes[name] || [])),
    [selectedModules, solutionDtcIndexes]
  );
  const dtcCharged = useMemo(
    () => Array.from(dtcOffSet).some((index) => !includedDtcs.has(index)),
    [dtcOffSet, includedDtcs]
  );
  const dtcSelectedPairs = useMemo(
    () => Array.from(dtcOffSet).flatMap((i) => dtcRowToPairs(dtcRows[i])),
    [dtcOffSet, dtcRows]
  );

  // The rows switched off — what the "DTC OFF" button counts and its modal lists.
  const offDtcRows = useMemo(
    () =>
      Array.from(dtcOffSet)
        .sort((a, b) => a - b)
        .map((index) => ({ row: dtcRows[index], index }))
        .filter(({ row }) => row),
    [dtcOffSet, dtcRows]
  );
  const visibleOffDtcRows = useMemo(() => {
    const q = offDtcSearch.trim().toLowerCase();
    if (!q) return offDtcRows;
    return offDtcRows.filter(
      ({ row }) =>
        (row.code1 || "").toLowerCase().includes(q) ||
        (row.code2 || "").toLowerCase().includes(q) ||
        (row.description || "").toLowerCase().includes(q)
    );
  }, [offDtcRows, offDtcSearch]);

  const totalCost = useMemo(() => {
    let total = 0;
    for (const [name, credits] of moduleEntries) {
      if (selectedModules.has(name) && credits != null) total += credits;
    }
    for (const tuning of tunings) {
      if (selectedTunings.has(tuning.id) && tuning.credits != null) total += tuning.credits;
    }
    if (dtcCharged && pricing.dtcOff != null) total += pricing.dtcOff;
    return total;
  }, [moduleEntries, tunings, selectedModules, selectedTunings, dtcCharged, pricing.dtcOff]);

  const nothingSelected = selectedModules.size === 0 && selectedTunings.size === 0 && !dtcActive;
  const dtcDisabled = pricing.dtcOff == null;

  const infoFields = [
    // /check reports the identified pair as ecuinfo.systemManufacturer (brand) and
    // ecuinfo.ecuId (ECU) — the same two values /patch takes as `brand` and `ecu`.
    ["Brand", info.systemManufacturer],
    ["Model", info.vehicleModel],
    ["ECU", info.ecuId],
    ["Software #", swNumber],
    ["Hardware #", info.hardwareNumber],
    ["Engine", info.engineDesignation],
    ["Power", info.enginePowerKw ? `${info.enginePowerKw} kW` : ""],
    ["Torque", info.engineTorque ? `${info.engineTorque} Nm` : ""],
    ["Emission", info.emissionStandard],
    ["Year", info.vehicleYear ? String(info.vehicleYear) : ""],
  ].filter(([, v]) => v);

  const resetResults = () => {
    setQuote(null);
    setPatchResult(null);
    setNeedsSelection(false);
    setEcuCandidates([]);
    setIsEcuModalOpen(false);
    setSelectedModules(new Set());
    setSelectedTunings(new Set());
    setDtcOffSet(new Set());
    setDtcSearch("");
  };

  const runCheck = async (theFile, theBrand, theEcu) => {
    if (!theFile) return;
    setError("");
    setChecking(true);
    setQuote(null);
    setPatchResult(null);
    try {
      const result = await checkEcu({ file: theFile, brand: theBrand, ecu: theEcu });
      // The same selection payload also reaches us on a 2xx if the server ever
      // stops flagging it as a 409 — without this it would render as an empty
      // workspace with no solutions, no DTCs and no explanation.
      if (isEcuSelectionPayload(result)) {
        openEcuSelection(result);
        return;
      }
      setQuote(result);
      setNeedsSelection(false);
      setEcuCandidates([]);
    } catch (err) {
      const body = err?.response?.data;
      if (err?.response?.status === 409 || isEcuSelectionPayload(body)) {
        openEcuSelection(body);
      } else {
        setError(await extractApiError(err, "Identification failed. Please try again."));
      }
    } finally {
      setChecking(false);
    }
  };

  // Several equally-specific ECUs match the file. The server names them —
  //   { requiresEcuSelection, message, ecuCandidates: [{ brand, ecu }, …] }
  // — so the customer picks a pair from the list instead of guessing the exact
  // spelling the engine expects. The typed brand/ECU stays available underneath
  // for the case where the body named none.
  const openEcuSelection = (payload) => {
    const candidates = extractEcuCandidates(payload);
    const message =
      (typeof payload?.message === "string" && payload.message.trim()) ||
      "Multiple ECUs match this file.";
    setEcuCandidates(candidates);
    setEcuSelectionMessage(message);
    setNeedsSelection(true);
    if (candidates.length > 0) {
      setIsEcuModalOpen(true);
      // The modal carries the explanation; a red strip behind it would just
      // repeat it and stay behind after a pick.
      setError("");
    } else {
      setError(`${message} Enter the brand and ECU, then identify again.`);
    }
  };

  // Picking a candidate is the same resubmit the manual inputs do — same file,
  // this time with the brand/ECU pair chosen from the list the server offered.
  const handleCandidateSelect = (candidate) => {
    setIsEcuModalOpen(false);
    setBrand(candidate.brand);
    setEcu(candidate.ecu);
    runCheck(file, candidate.brand, candidate.ecu);
  };

  const handleSelectedFile = async (nextFile) => {
    if (!nextFile) return;
    // Only a raw ECU binary is accepted — anything we can positively recognise as
    // something else is turned away here, before it is ever uploaded.
    const rejection = await ecuBinaryRejection(nextFile);
    if (rejection) {
      setFileError(rejection);
      return;
    }
    setFile(nextFile);
    setBrand("");
    setEcu("");
    resetResults();
    runCheck(nextFile, "", "");
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!busy) setIsDragging(true);
  };
  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    if (busy) return;
    handleSelectedFile(event.dataTransfer.files?.[0] || null);
  };

  const flip = (setFn) => (key) =>
    setFn((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const toggleModule = flip(setSelectedModules);
  const toggleTuning = flip(setSelectedTunings);
  const toggleDtc = flip(setDtcOffSet);

  // Only allow turning something on if the customer can afford the added cost;
  // otherwise pop the appropriate modal. Turning things off is always allowed.
  const requestSelect = (addedCost, apply) => {
    if (unlimited || addedCost <= 0 || creditBalance == null) return apply();
    if (creditBalance <= 0) return setModalMsg("spent");
    if (totalCost + addedCost > creditBalance) return setModalMsg("notEnough");
    return apply();
  };
  // Selecting a solution also turns off the DTCs linked to it (its DTC group), like EcuMatic. Those
  // DTCs are part of the solution's price — no DTC-off charge for them.
  const selectModule = (name) => {
    const linked = solutionDtcIndexes[name] || [];
    const newDtcs = linked.filter((index) => !dtcOffSet.has(index));
    const added = pricing.modules?.[name] ?? 0;
    requestSelect(added, () => {
      toggleModule(name);
      if (newDtcs.length > 0) {
        setDtcOffSet((prev) => new Set([...prev, ...newDtcs]));
      }
    });
  };
  // Deselecting turns its linked DTCs back on — except ones another selected solution also needs.
  const deselectModule = (name) => {
    toggleModule(name);
    const stillNeeded = new Set(
      Array.from(selectedModules)
        .filter((other) => other !== name)
        .flatMap((other) => solutionDtcIndexes[other] || [])
    );
    const release = (solutionDtcIndexes[name] || []).filter((index) => !stillNeeded.has(index));
    if (release.length > 0) {
      setDtcOffSet((prev) => {
        const next = new Set(prev);
        release.forEach((index) => next.delete(index));
        return next;
      });
    }
  };
  // A solution with an installer note or a DTC group is confirmed in a dialog showing both first.
  const onToggleModule = (name) =>
    selectedModules.has(name)
      ? deselectModule(name)
      : solutionNotes[name] || solutionDtcs[name]
      ? setPendingNote({ name, note: solutionNotes[name] || "", dtcs: solutionDtcs[name] || [] })
      : selectModule(name);
  const onToggleTuning = (tuning) =>
    selectedTunings.has(tuning.id)
      ? toggleTuning(tuning.id)
      : requestSelect(tuning.credits ?? 0, () => toggleTuning(tuning.id));
  const onToggleDtcRow = (index) =>
    dtcOffSet.has(index)
      ? toggleDtc(index)
      : requestSelect(dtcCharged || includedDtcs.has(index) ? 0 : pricing.dtcOff ?? 0, () => toggleDtc(index));

  const handlePatch = async () => {
    if (nothingSelected) {
      setError("Toggle at least one solution, tuning, or DTC.");
      return;
    }
    if (!unlimited && creditBalance != null && totalCost > creditBalance) {
      setModalMsg(creditBalance <= 0 ? "spent" : "notEnough");
      return;
    }
    setError("");
    setPatching(true);
    setPatchResult(null);
    try {
      const result = await patchEcu({
        file,
        brand: brand || info.systemManufacturer,
        ecu: ecu || info.ecuId,
        swNumber,
        modules: Array.from(selectedModules),
        tuningIds: Array.from(selectedTunings),
        dtcPairs: dtcSelectedPairs,
      });
      downloadBlob(result.blob, result.fileName);
      setPatchResult({ fileName: result.fileName, charged: result.charged, balance: result.balance });
      refreshBalance();
    } catch (err) {
      setError(await extractApiError(err, "Patch failed. Please try again."));
    } finally {
      setPatching(false);
    }
  };

  const selectionCount = selectedModules.size + selectedTunings.size + (dtcActive ? 1 : 0);

  const statusMessage = checking
    ? "Identifying file and loading DTC data…"
    : patching
    ? "Patching file…"
    : !file
    ? "Click to upload or drag & drop an ECU file — identification is free"
    : selectionCount > 0
    ? `${selectionCount} change${selectionCount === 1 ? "" : "s"} ready to patch · ${totalCost} CRS`
    : quote
    ? "Toggle a solution, tuning, or DTC to apply it"
    : "Ready.";

  const hasSolutions = moduleEntries.length > 0;
  const hasTunings = tunings.length > 0;
  const creditsDisplay = unlimited ? "∞" : creditBalance == null ? "—" : creditBalance;

  return (
    <div
      className={`mx-auto max-w-[1500px] space-y-5 rounded-[26px] px-4 pb-4 pt-0 transition sm:px-6 sm:pb-6 ${
        isDragging ? "ring-4 ring-inset ring-red-500/30" : ""
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        className="sr-only"
        onChange={(e) => {
          const picked = e.target.files?.[0] || null;
          // Clear the input so picking the same file again — after a rejection,
          // typically — still fires onChange.
          e.target.value = "";
          handleSelectedFile(picked);
        }}
      />

      {requestSent ? (
        <div className="flex items-start gap-2 rounded-[14px] border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-700 dark:text-emerald-300">
          <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0" />
          <span className="flex-1">
            Your support request was sent. Follow the replies in{" "}
            <Link to="/automatic-support" className="font-semibold underline">
              Automatic Support
            </Link>
            .
          </span>
          <button type="button" onClick={() => setRequestSent(false)} aria-label="Dismiss">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      ) : null}

      {error ? (
        <div className="flex items-start gap-2 rounded-[14px] border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-700 dark:text-rose-300">
          <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" />
          <span>{error}</span>
        </div>
      ) : null}

      {/* Solutions & Tuning + the status/patch bar */}
      <section className={`${PANEL} p-5 sm:p-6`}>
        <SectionHeading
          title="Solutions & Tuning"
          badge={selectionCount > 0 ? <Badge tone="danger">{selectionCount} selected</Badge> : null}
        >
          {needsSelection ? (
            <>
              {ecuCandidates.length > 0 ? (
                <button type="button" onClick={() => setIsEcuModalOpen(true)} className={GHOST}>
                  <SparklesIcon className="h-4 w-4" />
                  Choose ECU
                </button>
              ) : null}
              <input
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                placeholder="BRAND"
                className={`h-10 w-36 rounded-full border px-4 text-sm outline-none transition focus:border-red-400 focus:ring-4 focus:ring-red-500/10 ${CONTROL}`}
              />
              <input
                value={ecu}
                onChange={(e) => setEcu(e.target.value)}
                placeholder="ECU"
                className={`h-10 w-36 rounded-full border px-4 text-sm outline-none transition focus:border-red-400 focus:ring-4 focus:ring-red-500/10 ${CONTROL}`}
              />
              <button
                type="button"
                disabled={checking}
                onClick={() => runCheck(file, brand, ecu)}
                className={buttonClass({ size: "sm" })}
              >
                {checking ? <Spinner /> : <SparklesIcon className="h-4 w-4" />}
                {checking ? "Identifying…" : "Identify again"}
              </button>
            </>
          ) : null}

          {file ? (
            <span
              className={`inline-flex h-9 max-w-[220px] items-center gap-2 rounded-full border px-3 text-xs font-semibold ${CONTROL}`}
              title={file.name}
            >
              <DocumentTextIcon className="h-4 w-4 shrink-0 text-red-500 dark:text-orange-400" />
              <span className="truncate">{file.name}</span>
            </span>
          ) : null}
          <CreditsPill value={creditsDisplay} />
          <button
            type="button"
            disabled={!file || busy}
            onClick={() => {
              setRequestSent(false);
              setIsRequestOpen(true);
            }}
            title={file ? "Ask Automatic support about this file" : "Upload a file first"}
            className={buttonClass({ variant: "ghost", size: "sm" })}
          >
            <LifebuoyIcon className="h-4 w-4" />
            Support request
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => fileInputRef.current?.click()}
            className={buttonClass({ size: "sm" })}
          >
            <ArrowUpTrayIcon className="h-4 w-4" />
            {file ? "Change file" : "Upload file"}
          </button>
        </SectionHeading>

        {!quote ? (
          /* Nothing identified yet: the full catalogs render inert, so the page
             says what the service can do while it waits for a file. */
          <>
            <div className="mt-5">
              <div className="mb-2.5 flex flex-wrap items-center justify-between gap-3">
                <GroupLabel>Tunings</GroupLabel>
                <GroupLabel>Upload a file to see what applies to it</GroupLabel>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8">
                {TUNING_STAGES.map((name) => (
                  <ToggleTile
                    key={name}
                    label={name}
                    price={null}
                    disabled
                    active={false}
                    onToggle={() => {}}
                    icon={<TuningIcon name={name} className="h-4 w-4" />}
                  />
                ))}
              </div>
            </div>

            <div className="my-5 h-px bg-gray-200 dark:bg-[#2c2c2e]" />

            <div>
              <GroupLabel className="mb-2.5">Solutions</GroupLabel>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8">
                {SOLUTION_MODULES.map((name) => (
                  <ToggleTile
                    key={name}
                    label={name}
                    price={null}
                    disabled
                    active={false}
                    onToggle={() => {}}
                    icon={<SolutionIcon name={name} className="h-4 w-4" />}
                  />
                ))}
              </div>
            </div>
          </>
        ) : !hasSolutions && !hasTunings ? (
          <EmptyState
            className="mt-5"
            title="Not supported"
            hint="This file has no solutions or tunings available."
          />
        ) : (
          <>
            {hasTunings ? (
              <div className="mt-5">
                <GroupLabel className="mb-2.5">Tunings</GroupLabel>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8">
                  {tunings.map((tuning) => (
                    <ToggleTile
                      key={tuning.id}
                      label={tuning.name}
                      price={tuning.credits}
                      disabled={tuning.credits == null}
                      alreadyOff={tuningsAlreadyOff.has(tuning.name)}
                      active={selectedTunings.has(tuning.id)}
                      onToggle={() => onToggleTuning(tuning)}
                      icon={<TuningIcon name={tuning.name} className="h-4 w-4" />}
                    />
                  ))}
                </div>
              </div>
            ) : null}

            {hasTunings && hasSolutions ? (
              <div className="my-5 h-px bg-gray-200 dark:bg-[#2c2c2e]" />
            ) : null}

            {hasSolutions ? (
              <div className={hasTunings ? "" : "mt-5"}>
                <GroupLabel className="mb-2.5">Solutions</GroupLabel>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8">
                  {moduleEntries.map(([name, credits]) => (
                    <ToggleTile
                      key={name}
                      label={name}
                      price={credits}
                      disabled={credits == null}
                      alreadyOff={solutionsAlreadyOff.has(name)}
                      active={selectedModules.has(name)}
                      note={solutionNotes[name]}
                      onToggle={() => onToggleModule(name)}
                      icon={<SolutionIcon name={name} className="h-4 w-4" />}
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </>
        )}

        {/* Status + patch bar */}
        <div className="mt-5 flex flex-wrap items-center gap-3">
          {busy ? (
            <div
              role="status"
              aria-live="polite"
              className="vdp-loading-bar flex min-h-11 flex-1 items-center justify-center gap-2 rounded-full px-4 text-sm font-semibold text-white"
            >
              <Spinner />
              {statusMessage}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className={`flex min-h-11 flex-1 items-center justify-center rounded-full border border-dashed px-4 text-center text-sm font-semibold text-gray-500 transition hover:border-red-400/60 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200 ${SUNKEN}`}
            >
              {statusMessage}
            </button>
          )}

          {quote ? (
            <div className={`flex h-11 items-center gap-2 rounded-full border px-4 ${CONTROL}`}>
              <span className="text-xs font-medium uppercase tracking-[0.08em] text-gray-400 dark:text-slate-500">
                Total
              </span>
              <span className="bg-gradient-to-r from-red-600 to-orange-500 bg-clip-text text-lg font-extrabold text-transparent">
                {totalCost}
              </span>
              <span className="text-xs font-semibold text-gray-400 dark:text-slate-500">CRS</span>
            </div>
          ) : null}

          <button
            type="button"
            onClick={handlePatch}
            disabled={patching || nothingSelected}
            className={
              patching
                ? "vdp-loading-bar inline-flex h-11 items-center justify-center gap-2 rounded-full px-10 text-sm font-bold text-white"
                : `${PRIMARY} px-10`
            }
          >
            {patching ? <Spinner /> : <ArrowDownTrayIcon className="h-4 w-4" />}
            {patching ? "Patching…" : "Patch"}
          </button>
        </div>

        {Array.from(selectedModules).some((name) => solutionNotes[name]) ? (
          <div className="mt-4 space-y-1.5 rounded-[14px] border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
            <p className="flex items-center gap-1.5 font-bold">
              <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
              Before you patch
            </p>
            {Array.from(selectedModules)
              .filter((name) => solutionNotes[name])
              .map((name) => (
                <div key={name}>
                  <p className="font-semibold">{name}:</p>
                  <NoteContent note={solutionNotes[name]} className="mt-0.5" />
                </div>
              ))}
          </div>
        ) : null}

        {patchResult ? (
          <div className="mt-4 flex items-start gap-2 rounded-[14px] border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-700 dark:text-emerald-300">
            <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0" />
            <span>
              Patched <span className="font-bold">“{patchResult.fileName}”</span> — downloaded successfully.
              {patchResult.charged ? ` Charged ${patchResult.charged} CRS.` : ""}
            </span>
          </div>
        ) : null}
      </section>

      {/* DTC table + file identification */}
      <div className="grid gap-5 xl:grid-cols-2">
        <section className={`${PANEL} flex min-h-[560px] flex-col p-5`}>
          <SectionHeading title="DTC Table" badge={dtcRows.length > 0 ? <Badge>{dtcRows.length}</Badge> : null}>
            <button
              type="button"
              onClick={() => {
                setOffDtcSearch("");
                setIsOffDtcModalOpen(true);
              }}
              disabled={offDtcRows.length === 0}
              className="inline-flex h-9 items-center gap-2 rounded-full border border-rose-400/50 bg-rose-400/10 px-4 text-xs font-bold uppercase tracking-[0.08em] text-rose-600 transition hover:border-rose-400 hover:bg-rose-400/20 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-400 dark:text-rose-300 dark:disabled:border-[#3a3a3d] dark:disabled:bg-[#262628] dark:disabled:text-slate-500"
            >
              DTC OFF
              <span
                className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${
                  offDtcRows.length > 0
                    ? "bg-rose-400/20 text-rose-600 dark:text-rose-300"
                    : "bg-gray-100 text-gray-400 dark:bg-white/10 dark:text-slate-400"
                }`}
              >
                {offDtcRows.length}
              </span>
            </button>
          </SectionHeading>

          <input
            value={dtcSearch}
            onChange={(e) => setDtcSearch(e.target.value)}
            placeholder="Search DTC or description"
            disabled={dtcRows.length === 0}
            className={`mt-4 h-10 w-full rounded-full border px-4 text-sm outline-none transition focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 ${CONTROL}`}
          />

          <div className={`mt-3 flex-1 overflow-hidden rounded-[14px] border ${SUNKEN}`}>
            {dtcRows.length === 0 ? (
              <div className="px-4 py-8 text-sm text-gray-400 dark:text-slate-500">
                {checking
                  ? "Reading DTC data…"
                  : quote
                  ? "DTC not supported for this file."
                  : "Upload a file to load DTC data."}
              </div>
            ) : (
              <div className="max-h-[560px] overflow-auto">
                <table className="min-w-full border-separate border-spacing-0 text-sm">
                  <thead>
                    <tr className="sticky top-0 z-10">
                      {["#", "Status", hasDtc2 ? "DTC1" : "DTC", ...(hasDtc2 ? ["DTC2"] : []), "Description"].map(
                        (head) => (
                          <th
                            key={head}
                            className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400"
                          >
                            {head}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleDtcRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={hasDtc2 ? 5 : 4}
                          className="px-4 py-8 text-center text-sm text-gray-400 dark:text-slate-500"
                        >
                          No DTCs match “{dtcSearch}”.
                        </td>
                      </tr>
                    ) : null}
                    {visibleDtcRows.map(({ row, index }) => {
                      // A row the check already reports as OFF reads off and
                      // is locked: it is gone from the file, not a selection.
                      const alreadyOff = row.alreadyOff;
                      const off = alreadyOff || dtcOffSet.has(index);
                      const cell = `border-t px-4 py-2 transition ${
                        off
                          ? "border-rose-500/20 bg-rose-500/10"
                          : "border-gray-200 bg-white dark:border-[#2c2c2e] dark:bg-[#1c1c1e]"
                      }`;
                      return (
                        <tr key={`${row.code1}-${row.code2}-${index}`}>
                          <td className={`${cell} font-mono text-xs text-gray-400 dark:text-slate-500`}>
                            {index + 1}
                          </td>
                          <td className={cell}>
                            <Switch
                              checked={!off}
                              disabled={dtcDisabled || alreadyOff}
                              onToggle={() => onToggleDtcRow(index)}
                              label={`${row.code1 || row.code2} DTC`}
                            />
                          </td>
                          <td className={cell}>
                            <DtcCodeChip code={row.code1} off={off} />
                          </td>
                          {hasDtc2 ? (
                            <td className={cell}>
                              <DtcCodeChip code={row.code2} off={off} />
                            </td>
                          ) : null}
                          <td
                            className={`${cell} min-w-[200px] break-words ${
                              off ? "text-rose-700 dark:text-rose-200" : "text-gray-600 dark:text-slate-400"
                            }`}
                          >
                            {row.description || ""}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        <section className={`${PANEL} min-h-[560px] p-5`}>
          <SectionHeading title="File Identification" />
          {!quote && !file ? (
            <EmptyState className="mt-4" title="Nothing identified yet" hint="Upload a file to detect its info." />
          ) : (
            <div className={`mt-4 rounded-[16px] border p-4 ${SUNKEN}`}>
              <div className="grid gap-3 sm:grid-cols-2">
                {file ? <InfoTile label="File Name" value={file.name} accent /> : null}
                {infoFields.map(([label, value]) => (
                  <InfoTile key={label} label={label} value={value} />
                ))}
              </div>
              {!quote ? (
                <p className="mt-4 text-sm text-gray-400 dark:text-slate-500">
                  {checking ? "Detecting ECU information…" : "No identification details."}
                </p>
              ) : null}
            </div>
          )}
        </section>
      </div>

      {/* Toggled-OFF DTCs */}
      {isOffDtcModalOpen ? createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Toggled OFF DTCs"
          onClick={() => setIsOffDtcModalOpen(false)}
        >
          <div
            className={`flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-[18px] border shadow-2xl ${PANEL}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-5 py-4 dark:border-[#2c2c2e] dark:bg-[#161617]">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold uppercase tracking-[0.08em] text-rose-600 dark:text-rose-300">
                  Toggled OFF DTCs
                </h3>
                <span className="rounded-full bg-rose-400/20 px-2 py-0.5 text-xs font-bold text-rose-600 dark:text-rose-300">
                  {offDtcRows.length}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setIsOffDtcModalOpen(false)}
                aria-label="Close"
                className={`inline-flex h-8 w-8 items-center justify-center rounded-full border transition hover:border-rose-400/60 hover:text-rose-500 ${CONTROL}`}
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
            <div className="border-b border-gray-200 px-5 py-3 dark:border-[#2c2c2e]">
              <input
                value={offDtcSearch}
                onChange={(e) => setOffDtcSearch(e.target.value)}
                placeholder="Search DTC or description"
                className={`h-10 w-full rounded-full border px-4 text-sm outline-none transition focus:border-red-400 focus:ring-4 focus:ring-red-500/10 ${CONTROL}`}
              />
            </div>
            <div className="overflow-auto p-3">
              {visibleOffDtcRows.length === 0 ? (
                <p className="px-2 py-8 text-center text-sm text-gray-400 dark:text-slate-500">
                  {offDtcRows.length === 0 ? "No DTCs are switched off." : `No DTCs match “${offDtcSearch}”.`}
                </p>
              ) : (
                <ul className="space-y-2">
                  {visibleOffDtcRows.map(({ row, index }) => (
                    <li
                      key={`off-${index}`}
                      className="flex items-center gap-3 rounded-[12px] border border-rose-500/20 bg-rose-500/10 px-3 py-2"
                    >
                      <Switch
                        checked={false}
                        onToggle={() => onToggleDtcRow(index)}
                        label={`${row.code1 || row.code2} DTC`}
                      />
                      <DtcCodeChip code={row.code1} off />
                      {hasDtc2 ? <DtcCodeChip code={row.code2} off /> : null}
                      <span className="min-w-0 flex-1 break-words text-sm text-rose-700 dark:text-rose-200">
                        {row.description || ""}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>,
        document.body
      ) : null}

      {/* Several ECUs match the file — pick one */}
      {isEcuModalOpen && ecuCandidates.length > 0 ? (
        (() => {
          // Candidates usually share one ECU and differ only by brand, so present a
          // button per brand; when the ECU itself varies, show it beneath each brand
          // so no candidate is collapsed away.
          const distinctEcus = Array.from(
            new Set(ecuCandidates.map((candidate) => candidate.ecu).filter(Boolean))
          );
          const showEcuSubLabel = distinctEcus.length > 1;
          return createPortal(
            <div
              className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4"
              role="dialog"
              aria-modal="true"
              aria-label="Choose ECU"
              onClick={() => setIsEcuModalOpen(false)}
            >
              <div
                className={`w-full max-w-md overflow-hidden rounded-[18px] border border-red-500/50 shadow-2xl ${PANEL}`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center gap-3 border-b border-gray-200 bg-gray-50 px-5 py-4 dark:border-[#2c2c2e] dark:bg-[#161617]">
                  <h3 className="flex-1 text-sm font-bold uppercase tracking-[0.12em] text-red-600 dark:text-orange-400">
                    Choose ECU
                  </h3>
                  <button
                    type="button"
                    onClick={() => setIsEcuModalOpen(false)}
                    aria-label="Close"
                    className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition hover:border-red-400/60 hover:text-red-500 ${CONTROL}`}
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
                <div className="px-6 py-5 text-center">
                  <p className="text-sm font-medium text-gray-500 dark:text-slate-400">
                    {ecuSelectionMessage || "This file matches more than one ECU."}
                  </p>
                  <p className="mt-1.5 text-xs text-gray-400 dark:text-slate-500">
                    If it isn't listed, close this window and enter the brand and ECU manually.
                  </p>
                  <div className="mt-5 flex flex-wrap justify-center gap-3">
                    {ecuCandidates.map((candidate, index) => (
                      <button
                        type="button"
                        key={`${candidate.brand}||${candidate.ecu}||${index}`}
                        onClick={() => handleCandidateSelect(candidate)}
                        className="min-w-[140px] rounded-[12px] border border-red-500/50 bg-gray-50 px-5 py-3 text-sm font-bold text-red-600 transition hover:border-transparent hover:bg-gradient-to-r hover:from-red-600 hover:to-orange-500 hover:text-white dark:bg-[#161617] dark:text-orange-400"
                      >
                        {candidate.brand || candidate.ecu}
                        {showEcuSubLabel && candidate.ecu ? (
                          <span className="mt-0.5 block text-[11px] font-medium normal-case text-current opacity-70">
                            {candidate.ecu}
                          </span>
                        ) : null}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>,
            document.body
          );
        })()
      ) : null}

      {/* Rejected upload — not a raw ECU binary */}
      {fileError ? createPortal(
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4"
          role="alertdialog"
          aria-modal="true"
          aria-label="Invalid file"
          onClick={() => setFileError("")}
        >
          <div
            className={`w-full max-w-md overflow-hidden rounded-[18px] border border-rose-500/60 shadow-2xl ${PANEL}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2.5 px-5 pb-4 pt-5">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-rose-500/15 text-rose-500">
                <ExclamationTriangleIcon className="h-4 w-4" />
              </span>
              <h3 className="text-sm font-bold uppercase tracking-[0.12em] text-rose-600 dark:text-rose-400">
                Invalid file
              </h3>
            </div>
            <div className="border-y border-gray-200 px-5 py-4 text-sm font-medium leading-relaxed text-gray-700 dark:border-[#2c2c2e] dark:text-slate-200">
              {fileError}
            </div>
            <div className="flex justify-end px-5 py-4">
              <button
                type="button"
                autoFocus
                onClick={() => setFileError("")}
                className="h-10 rounded-full bg-rose-500 px-8 text-sm font-bold text-white transition hover:bg-rose-400"
              >
                OK
              </button>
            </div>
          </div>
        </div>,
        document.body
      ) : null}

      {/* Installer note to confirm before a solution is selected */}
      {/* Portalled to <body>: inside the page an ancestor clips the fixed overlay, leaving the top uncovered. */}
      {pendingNote ? createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setPendingNote(null)}
        >
          <div
            className={`flex max-h-[85vh] w-full flex-col overflow-hidden rounded-[18px] border shadow-2xl ${PANEL} ${
              pendingNote.dtcs.length > 0 ? "max-w-2xl" : "max-w-md"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 pb-4">
              <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-white">
                {pendingNote.dtcs.length > 0 ? `${pendingNote.name} DTCs` : `Before you patch ${pendingNote.name}`}
                {pendingNote.dtcs.length > 0 ? <Badge>{pendingNote.dtcs.length}</Badge> : null}
              </h3>
              {pendingNote.dtcs.length > 0 ? (
                <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
                  Switching {pendingNote.name} off also turns these DTCs off in the patch.
                </p>
              ) : null}
              {pendingNote.note ? (
                <div className="mt-3 flex max-h-[55vh] items-start gap-2 overflow-y-auto rounded-[12px] border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-sm text-amber-800 dark:text-amber-200">
                  <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
                  <NoteContent note={pendingNote.note} className="min-w-0 flex-1" />
                </div>
              ) : null}
            </div>
            {pendingNote.dtcs.length > 0 ? (
              <div className="min-h-0 flex-1 overflow-y-auto border-t border-gray-200 px-6 py-2 dark:border-[#2c2c2e]">
                {pendingNote.dtcs.map((row, index) => (
                  <div
                    key={`${row.code1}-${row.code2}-${index}`}
                    className="flex items-center gap-3 border-b border-gray-100 py-2 last:border-0 dark:border-[#232325]"
                  >
                    <DtcCodeChip code={row.code1} off={!row.alreadyOff} />
                    {row.code2 ? <DtcCodeChip code={row.code2} off={!row.alreadyOff} /> : null}
                    <span className="min-w-0 flex-1 truncate text-sm text-gray-600 dark:text-slate-300" title={row.description}>
                      {row.description || "—"}
                    </span>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                        row.alreadyOff
                          ? "bg-gray-100 text-gray-500 dark:bg-[#262628] dark:text-slate-400"
                          : "bg-rose-500/15 text-rose-600 dark:text-rose-300"
                      }`}
                    >
                      {row.alreadyOff ? "Already off" : "Turns off"}
                    </span>
                  </div>
                ))}
              </div>
            ) : null}
            <div className="flex justify-end gap-3 border-t border-gray-200 p-4 dark:border-[#2c2c2e]">
              <button type="button" onClick={() => setPendingNote(null)} className={GHOST}>
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const { name } = pendingNote;
                  setPendingNote(null);
                  selectModule(name);
                }}
                className={PRIMARY}
              >
                {pendingNote.note ? "I understand" : "Confirm"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      ) : null}

      {/* Credit guards */}
      {modalMsg ? createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setModalMsg("")}
        >
          <div
            className={`w-full max-w-sm rounded-[18px] border p-6 text-center shadow-2xl ${PANEL}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-rose-500/15 text-rose-500">
              <ExclamationTriangleIcon className="h-6 w-6" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              {modalMsg === "spent" ? "You spent all your credits" : "Not enough credits"}
            </h3>
            <p className="mt-1.5 text-sm text-gray-500 dark:text-slate-400">
              {modalMsg === "spent"
                ? "You have no credits left. Buy more to continue."
                : "You don't have enough credits for this selection."}
            </p>
            <div className="mt-5 flex justify-center gap-3">
              <button type="button" onClick={() => setModalMsg("")} className={GHOST}>
                Close
              </button>
              <Link to="/buy-credits" onClick={() => setModalMsg("")} className={PRIMARY}>
                Buy credits
              </Link>
            </div>
          </div>
        </div>,
        document.body
      ) : null}

      {isRequestOpen && file ? (
        <EcuTicketRequestModal
          file={file}
          brand={info.systemManufacturer || brand}
          ecu={info.ecuId || ecu}
          onSolutions={extractOnSolutions(quote?.identification?.solutions)}
          onClose={() => setIsRequestOpen(false)}
          onCreated={() => {
            setIsRequestOpen(false);
            setRequestSent(true);
          }}
        />
      ) : null}
    </div>
  );
};

export default Automatic;
